/* eslint-disable complexity */
import React, { useCallback, useEffect, useRef } from 'react';
import clsx from 'clsx';
import { Grow, makeStyles } from '@material-ui/core';

import SweetAlert from '../../../../Components/Alerts/SweetAlert';

import { ALERT_TYPE } from '../../../../Components/Alerts/alert_enums';
import { Company } from '../../../../Configs/general';
import ContractAssociationFormAlias from './ContractAssociationFormAlias';
import ContractAssociationFormNotifications from './ContractAssociationFormNotifications/ContractAssociationFormNotifications';
import SimpleDivider from '../../../../Components/Dividers/SimpleDivider';
import ContractAssociationFormPhone from './ContractAssociationFormNotifications/ContractAssociationFormPhone';

const ContractAssociationFormGDC = props => {
  const {
    dialogMode,
    editMode,
    currentUser,
    contract,
    sendInvoice,
    setSendInvoice,
    isDigitalInvoice,
    setIsDigitalInvoice,
    digitalChannels,
    setDigitalChannels,
    aliasName,
    setAliasName,
    aliasIcon,
    setAliasIcon,
    loading,
    errors,
    needsPhoneValidation,
    setNeedsPhoneValidation,
    digitalInvoiceSweetAlertOnclick: handleSweetAlertClick,
    showPhoneInputPreferences,
    setCanSubmit = null,
    referredCode,
    setReferredCode = null,
    isReferredCodeRequired = false,
    setIsReferredCodeRequired = null,
    referredCodeEnable = false,
    allowedContractType,
    onPhoneValuesChange = null,
    phoneInputValues = {}
  } = props;

  // * OTHER HOOKS
  const classes = useStyles();

  // * REFS
  const needsValidationRef = useRef(null);

  // * GLOBAL VARS
  const digitalAlreadyActive = contract && contract.digitalInvoice;

  const showReferredInput = false;

  const handlePhoneValuesChange = useCallback(
    values => {
      if (!onPhoneValuesChange) {
        return;
      }

      onPhoneValuesChange(values);
    },
    [onPhoneValuesChange]
  );

  useEffect(() => {
    if (!allowedContractType && setCanSubmit) {
      setCanSubmit(true);
    }
  }, [allowedContractType, setCanSubmit]);

  const renderPhoneValidationNeededAlert = useCallback(() => {
    if (phoneInputValues.isValid && phoneInputValues.phone) {
      return (
        <SweetAlert message="Se te solicitará verificar tu número de celular antes de asociar." />
      );
    }

    if (needsPhoneValidation) {
      return (
        <Grow in={needsPhoneValidation}>
          <div
            className={clsx(classes.sweetAlertContainer, {
              [classes.sweetAlertContainerNoDialog]: !dialogMode
            })}
            ref={needsValidationRef}
          >
            <SweetAlert
              type={ALERT_TYPE.INFO}
              message={
                currentUser.phone ? (
                  <>
                    Al {editMode ? 'editar' : 'asociar'}{' '}
                    {Company.contractConjugation.regular.singular.demonstrative}{' '}
                    te podríamos solicitar validar el número (+
                    <span className={classes.textBold}>
                      {currentUser.phoneCountryCode} {currentUser.phone}
                    </span>
                    ).
                  </>
                ) : (
                  <>
                    Deberás agregar un número celular antes de asociar{' '}
                    {Company.contractConjugation.regular.singular.demonstrative}
                    .
                  </>
                )
              }
            />
          </div>
        </Grow>
      );
    }

    return <></>;
  }, [
    phoneInputValues.phone,
    phoneInputValues.isValid,
    needsPhoneValidation,
    classes,
    dialogMode,
    currentUser,
    editMode
  ]);

  return (
    <div className={classes.root}>
      <ContractAssociationFormAlias
        dialogMode={dialogMode}
        editMode={editMode}
        contract={contract}
        aliasName={aliasName}
        setAliasName={setAliasName}
        aliasIcon={aliasIcon}
        setAliasIcon={setAliasIcon}
        loading={loading}
        errors={errors}
      />

      {!currentUser.phone && showPhoneInputPreferences && (
        <ContractAssociationFormPhone
          currentUser={currentUser}
          loading={loading}
          onValuesChange={handlePhoneValuesChange}
          classes={classes}
        />
      )}

      <SimpleDivider withoutMargin className={classes.itemDivider} />

      {allowedContractType && (
        <ContractAssociationFormNotifications
          dialogMode={dialogMode}
          editMode={editMode}
          currentUser={currentUser}
          contract={contract}
          sendInvoice={sendInvoice}
          setSendInvoice={setSendInvoice}
          isDigitalInvoice={isDigitalInvoice}
          digitalAlreadyActive={digitalAlreadyActive}
          setIsDigitalInvoice={setIsDigitalInvoice}
          digitalChannels={digitalChannels}
          setDigitalChannels={setDigitalChannels}
          needsPhoneValidation={needsPhoneValidation}
          setNeedsPhoneValidation={setNeedsPhoneValidation}
          handleSweetAlertClick={handleSweetAlertClick}
          setCanSubmit={setCanSubmit}
          errors={errors}
          showReferredInput={showReferredInput}
          referredCodeEnable={referredCodeEnable}
          setReferredCode={setReferredCode}
          referredCode={referredCode}
          isReferredCodeRequired={isReferredCodeRequired}
          setIsReferredCodeRequired={setIsReferredCodeRequired}
        />
      )}

      {renderPhoneValidationNeededAlert()}
    </div>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
      marginBottom: theme.spacing(5)
    }
  },
  containerDialogMode: {
    paddingTop: 0
  },
  textBold: {
    fontWeight: 600
  },
  sweetAlertContainer: {
    width: '100%'
  },
  sweetAlertContainerNoDialog: {
    [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
      paddingBottom: theme.spacing(6)
    }
  },
  itemDivider: {
    marginTop: 0,
    marginBottom: 0
  },
  phoneInputContainer: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(3),
      justifyContent: 'center'
    }
  },
  countryCodeInput: {
    maxWidth: '30%',
    marginRight: theme.spacing(2)
  },
  phoneInput: {
    maxWidth: '70%'
  }
}));

export default ContractAssociationFormGDC;
